import { useState } from 'react'

import { EndTrialNowControllerService } from '@api'
import { getCurrency } from '@common/utils/currency.ts'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import { $account } from '@/models/account.ts'
import { loadCreatorsFx } from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { handleError } from '@/utils/error'

export const EndTrialSection = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [showEndTrialDialog, setShowEndTrialDialog] = useState(false)
  const account = useUnit($account)
  const { refetchAccount } = useAccount()

  const endTrialNowMutation = useMutation(async () => {
    return EndTrialNowControllerService.endTrialNow({ redirect: false })
  })

  const inTrial = account != null && account.inTrial

  const handleEndTrial = async () => {
    try {
      await endTrialNowMutation.mutateAsync()
      toast.success('Your trial has been ended.')
      setShowEndTrialDialog(false)
    } catch (error) {
      handleError(error, 'There was an error with ending your trial')
    } finally {
      endTrialNowMutation.reset()

      refetchAccount()
      loadCreatorsFx()
      queryClient.invalidateQueries(['owner', account?.userId])
      queryClient.invalidateQueries('licenses')
      queryClient.invalidateQueries('creator')
    }
  }

  if (!inTrial) {
    return null
  }

  return (
    <>
      <div className="mb-8 flex flex-col items-center justify-between gap-2 rounded-2xl bg-gray-200/50 px-5 py-5 sm:flex-row dark:bg-white/10">
        <div className="mr-3 flex flex-col">
          <h4 className="text-title text-xl font-semibold">
            End your trial now
          </h4>
          <p className="text-title text-sm font-medium">
            You can get more licenses by ending your trial and switching to a
            paid subscription.
          </p>
        </div>
        <Button onClick={() => setShowEndTrialDialog(true)}>
          End my trial
        </Button>
      </div>
      <Dialog
        show={showEndTrialDialog}
        onClose={() => setShowEndTrialDialog(false)}
      >
        <Dialog.Header>
          <Dialog.Title>End your trial now</Dialog.Title>
          <Dialog.Description>
            <div className="mb-2">
              Are you sure that you want to end your trial and pay{' '}
              <b>
                {getCurrency(
                  account!.trialPlanQuantity * account!.trialPlanPrice,
                )}
              </b>{' '}
              now?
            </div>
          </Dialog.Description>
          <Dialog.ClosableFooter
            closeText="Cancel"
            onClose={() => setShowEndTrialDialog(false)}
          >
            <Button
              isLoading={endTrialNowMutation.isLoading}
              onClick={handleEndTrial}
            >
              End trial
            </Button>
          </Dialog.ClosableFooter>
        </Dialog.Header>
      </Dialog>
    </>
  )
}
